import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const CRM_STATUSES = [
  'Open',
  'Follow Up Done',
  'Not Interested',
  'Closed - Won',
  'Closed - Lost'
];

export default function EventInquiriesModal({ open, onClose, trekId, eventName }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');


  const fromDate = new Date();
  fromDate.setMonth(fromDate.getMonth() - 4);
  const fromDateStr = fromDate.toISOString().split('T')[0];

  const { data, isLoading } = useQuery(
    ['admin-event-leads', trekId],
    () => api.get('/admin/crm/leads', { params: { trekId, fromDate: fromDateStr, limit: 200 } }),
    {
      enabled: open && !!trekId
    }
  );


  const leads = data?.data?.data || [];

  const updateLeadStatus = useMutation(
    ({ leadType, id, leadStatus }) =>
      api.put(`/admin/crm/leads/${leadType}/${id}`, { leadStatus }),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-event-leads', trekId])
    }
  );

  const deleteLead = useMutation(
    ({ leadType, id }) => api.delete(`/admin/crm/leads/${leadType}/${id}`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-event-leads', trekId])
    }
  );

  const handleStatusChange = (lead, newStatus) => {
    updateLeadStatus.mutate({ leadType: lead.leadType, id: lead.id, leadStatus: newStatus });
  };

  const handleDelete = (lead) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      deleteLead.mutate({ leadType: lead.leadType, id: lead.id });
    }
  };

  // Filter leads locally for search and status
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = (lead.name || '').toLowerCase().includes(search.toLowerCase()) || 
                          (lead.phone || '').includes(search);
    const matchesStatus = statusFilter ? lead.leadStatus === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Inquiries for {eventName || 'Event'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
          <TextField
            label="Search Leads"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 250 }}
          />
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>CRM Status</InputLabel>
            <Select
              value={statusFilter}
              label="CRM Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              {CRM_STATUSES.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeads.length > 0 ? (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>{lead.name}</TableCell>
                      <TableCell>{lead.phone}</TableCell>
                      <TableCell>{lead.leadType}</TableCell>
                      <TableCell>{new Date(lead.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={lead.leadStatus || ''}
                          onChange={(e) => handleStatusChange(lead, e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 150 }}
                        >
                          <MenuItem value=""><em>Pending</em></MenuItem>
                          {CRM_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => handleDelete(lead)}>
                          <Trash2 size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No inquiries found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
